/**
 * Kontrakt katalogu wzorów wydruków (kalka architektury z `email-templates`).
 *
 * Kluczowa decyzja — ta sama, co w poczcie: wzór deklaruje
 * `build(vars): PrintComposition`, czyli oddaje REPREZENTACJĘ POŚREDNIĄ (IR),
 * a nie gotowe bajty drukarki ani `<html>`. Bajty składają wyłącznie enkodery
 * (`encoders/zpl.ts` — jedyny plik z `^XA`; `encoders/epl.ts` — jedyny ze STX;
 * `encoders/escpos.ts` — jedyny z ESC @), a dokument HTML składa wyłącznie
 * `doc/layout.ts`. Dzięki temu nie istnieje wzór, który „ma własny EPL obok
 * PDF-a" — dokładnie ta klasa rozjazdu, przez którą etykieta GoodSpeed
 * w PackFlow istniała w dwóch wersjach i obie się rozjechały.
 *
 * Spójność jest wymuszona kształtem API, nie dobrą wolą autora — pilnuje jej
 * `tests/unit/packages/print-templates.test.ts`.
 */

import type { PrintComposition } from './ir';

/** Rodziny — grupowanie w panelu administracyjnym i w dokumentacji. */
export type PrintFamily =
  | 'logistics' // etykiety wysyłkowe, listy pakowe, paczki
  | 'production' // etykiety produkcyjne fabrica (ze śladem partii)
  | 'warehouse' // etykiety magazynowe, lokalizacje
  | 'documents' // faktury, WZ, dokumenty A4
  | 'haccp' // protokoły i rejestry HACCP
  | 'retail' // paragony, kwity (ESC/POS)
  | 'system'; // strony testowe, diagnostyka organu druku

export const FAMILY_LABEL: Record<PrintFamily, string> = {
  logistics: 'Logistyka',
  production: 'Produkcja',
  warehouse: 'Magazyn',
  documents: 'Dokumenty',
  haccp: 'HACCP',
  retail: 'Sprzedaż',
  system: 'System',
};

/**
 * Cele renderu. Wartości 1:1 z CHECK-iem `print_templates.target_format`
 * po migracji M1 (`623_print_sterowniki_systemowe.sql` dokłada `ESCPOS`).
 * `RAW` celowo NIE jest celem wzoru — `RAW` to format zadania z gotowym
 * payloadem od wołającego, a nie coś, co katalog potrafi wyprodukować.
 */
export type PrintTarget = 'ZPL' | 'EPL' | 'TSPL' | 'ESCPOS' | 'PDF';

/** Cele termiczne — payload to czysty string komend, render w całości u nas. */
export const THERMAL_TARGETS: readonly PrintTarget[] = ['ZPL', 'EPL', 'TSPL', 'ESCPOS'];

/** Strony dokumentów (cel PDF przez sterownik systemowy). */
export type DocPage = 'A4' | 'A5' | 'receipt80mm';

export type PrintMedia =
  | { kind: 'label'; widthMm: number; heightMm: number; dpi?: 203 | 300 }
  | { kind: 'document'; page: DocPage };

export interface PrintVariable {
  name: string;
  /** Do czego służy — trafia do dokumentacji i do edytora w panelu. */
  description: string;
  /** Wartość podstawiana w podglądzie. Musi być realistyczna, nie „foo". */
  example: unknown;
  /**
   * `barcode`/`qr` = zmienna, której wartość ląduje w kodzie kreskowym/QR —
   * jej charset waliduje enkoder (Code 128 nie przyjmie polskich znaków,
   * a EAN-13 niczego poza cyframi). `list` = tablica (także JSON w stringu,
   * jak w poczcie). `flag` = zmienna sterująca, nie wyświetlana.
   */
  kind?: 'text' | 'number' | 'url' | 'date' | 'list' | 'flag' | 'barcode' | 'qr' | 'image';
  /** Zmienna opcjonalna — wzór musi się wyrenderować, gdy jej nie ma. */
  optional?: boolean;
}

export type PrintVars = Record<string, unknown>;

/**
 * Klucze śladu produkcyjnego, którymi wzór może opisać podmiot etykiety.
 * LUSTRO zamkniętego słownika `KLUCZE_SLADU`
 * z `services/print-core/src/slad.ts` — test niezmienników wymusza, że każda
 * wartość tutaj istnieje TAM (pakiet nie importuje serwisu, żeby katalog
 * pozostał czystą biblioteką bez zależności od kodu Fastify).
 */
export type PrintSladKey = 'production_order_line_id' | 'batch_id' | 'production_stage_id';

export interface PrintTemplateDef {
  /**
   * Stały klucz wywołania; zadania z planu wzoru ustawiają
   * `print_jobs.document_type = slug`. Format `rodzina.nazwa` (z kropką) —
   * kropka jest celowa: skaner literałów katalogu e-mail dopuszcza wyłącznie
   * `[a-z0-9_-]`, więc slug z kropką nigdy nie koliduje z bramkami poczty.
   */
  slug: string;
  /** Nazwa w panelu. */
  name: string;
  family: PrintFamily;
  /** Organ systemu: „Fabrica", „Provisio", „NeuroSense", „Platforma"… */
  organ: string;
  /** Kiedy to się drukuje — jedno zdanie, trafia do dokumentacji. */
  trigger: string;
  media: PrintMedia;
  /** Które enkodery obsługują ten wzór. */
  targets: PrintTarget[];
  variables: PrintVariable[];
  /**
   * Wersja KONTRAKTU wzoru. Podbij, gdy zmieniasz zestaw zmiennych
   * nieopcjonalnych, media, cele albo klucze śladu — snapshot
   * w `tests/unit/packages/__snapshots__/print-templates-kontrakt.json`
   * wywala CI przy zmianie kontraktu bez podbicia (ta sama filozofia, co
   * brama `version:` w `pubspec.yaml` fabrica-panel). Render stempluje ją
   * jako `templateVersion` → `print_renditions.template_version` (M3).
   */
  version: number;
  /**
   * IR — NIGDY surowe bajty drukarki, NIGDY `<html>`. Test niezmienników
   * skanuje wynik `build()` i wywala się na `^XA`/STX/`<html`.
   */
  build(vars: PrintVars): PrintComposition;
  /**
   * Deklaracja, którymi kluczami zadania tego wzoru opisują ślad produkcyjny
   * (W12). Podzbiór zamkniętego słownika z `print-core/src/slad.ts`.
   */
  slad?: { keys: PrintSladKey[] };
}

// ---------------------------------------------------------------------------
// Pomocniki do czytania zmiennych — identyczna semantyka jak w e-mail.
// ---------------------------------------------------------------------------

/** Pobiera zmienną jako tekst — puste, gdy brak. */
export function s(v: PrintVars, key: string, fallback = ''): string {
  const raw = v[key];
  if (raw === null || raw === undefined || raw === '') return fallback;
  return String(raw);
}

/** Pobiera zmienną jako liczbę. */
export function n(v: PrintVars, key: string, fallback = 0): number {
  const raw = Number(v[key]);
  return Number.isFinite(raw) ? raw : fallback;
}

/**
 * Pobiera zmienną jako listę. Wołający przez REST przekazują listę jako JSON
 * w stringu (ta sama droga, co w poczcie z Edge Functions) — rozpakowujemy.
 */
export function list<T = unknown>(v: PrintVars, key: string): T[] {
  const raw = v[key];
  if (Array.isArray(raw)) return raw as T[];
  if (typeof raw === 'string' && raw.trim().startsWith('[')) {
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? (parsed as T[]) : [];
    } catch {
      return [];
    }
  }
  return [];
}

/** Czy zmienna ma wartość — do warunkowego dokładania węzłów. */
export function has(v: PrintVars, key: string): boolean {
  const raw = v[key];
  if (raw === null || raw === undefined) return false;
  if (typeof raw === 'string') return raw.trim().length > 0;
  if (Array.isArray(raw)) return raw.length > 0;
  return true;
}
